import { Controller } from '@hotwired/stimulus'
import { gsap } from 'gsap/dist/gsap'
import { SplitText } from 'gsap/dist/SplitText'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

// Shared timing for every section; mismatched durations or easings read as sluggish.
const MOTION = {
    duration: 0.55,
    ease: 'power3.out',
    y: 20,
    stagger: 0.07,
    start: 'top 85%',
}

// Beats in seconds from the section entering view.
const BEAT = {
    headline: 0,
    image: 0.1,
    subheading: 0.15,
    button: 0.25,
    accent: 0.3,
    textSwap: 1.2,
}

const IMAGE_DURATION = 0.7
const STROBE_REPEAT = 3

export default class extends Controller {
    static targets = [
        'headline',
        'subheading',
        'square',
        'smallSquare',
        'headerImage',
        'image',
        'digitalText',
        'button',
        'accordionItem',
    ]

    connect() {
        gsap.registerPlugin(SplitText, ScrollTrigger)
        ScrollTrigger.config({ limitCallbacks: true })

        this._splitTexts = []
        this.constructor._connectedControllers = (this.constructor._connectedControllers || 0) + 1

        this.mediaQueries = gsap.matchMedia()

        this.mediaQueries.add('(prefers-reduced-motion: reduce)', () => this.revealAll())

        this.mediaQueries.add('(prefers-reduced-motion: no-preference)', (context) => {
            // Two frames rather than a fixed timer: long enough for Turbo to finish
            // scrolling to top and layout to settle, without the hero sitting blank.
            this._initFrame = requestAnimationFrame(() => {
                this._initFrame = requestAnimationFrame(() => {
                    if (!this.element.isConnected) return
                    context.add(() => this.initAnimations())
                    this.constructor.debounceRefresh()
                })
            })
        })
    }

    static debounceRefresh() {
        if (this._refreshTimeout) clearTimeout(this._refreshTimeout)
        this._refreshTimeout = setTimeout(() => {
            this._refreshTimeout = null
            if ((this._connectedControllers || 0) === 0) return
            ScrollTrigger.refresh()
        }, 100)
    }

    revealAll() {
        this.element.querySelectorAll('.visibility-hidden').forEach((el) => {
            el.classList.remove('visibility-hidden')
        })
    }

    initAnimations() {
        const timeline = gsap.timeline({
            defaults: { duration: MOTION.duration, ease: MOTION.ease },
            scrollTrigger: { trigger: this.element, start: MOTION.start, once: true },
        })

        this.revealHeadlines(timeline)
        this.reveal(timeline, [...this.headerImageTargets, ...this.imageTargets], BEAT.image, {
            duration: IMAGE_DURATION,
        })
        this.reveal(timeline, this.subheadingTargets, BEAT.subheading)
        this.reveal(timeline, this.buttonTargets, BEAT.button)

        this.digitalTextTargets.forEach((target) => {
            const chars = this.splitIntoCharSpans(target)
            target.classList.remove('visibility-hidden')
            // Scale starts here, not in CSS, so clearProps can leave the characters as
            // plain inline text. A leftover blur(0) or scale(1) is still a filter/transform.
            gsap.set(chars, { opacity: 0, scale: 0.8 })
            timeline.to(
                chars,
                {
                    opacity: 1,
                    scale: 1,
                    duration: 0.3,
                    stagger: { each: 0.035, from: 'random' },
                    clearProps: 'transform',
                },
                BEAT.accent,
            )
        })

        this.strobe(timeline, this.squareTargets)
        this.strobe(timeline, this.smallSquareTargets)

        this.revealAccordionItems()
        this.initPatternStrobe()
    }

    reveal(timeline, targets, position, vars = {}) {
        if (!targets.length) return
        targets.forEach((el) => el.classList.remove('visibility-hidden'))

        timeline.from(
            targets,
            { opacity: 0, y: MOTION.y, stagger: MOTION.stagger, ...vars },
            position,
        )
    }

    revealHeadlines(timeline) {
        this.headlineTargets.forEach((target) => {
            target.classList.remove('visibility-hidden')

            const split = new SplitText(target, { type: 'lines', linesClass: 'overflow-hidden' })
            this._splitTexts.push(split)

            timeline.from(
                split.lines,
                { opacity: 0, y: MOTION.y, stagger: MOTION.stagger },
                BEAT.headline,
            )

            // Class lookup rather than a Stimulus target: the target list is sometimes
            // empty here because SplitText has already rewritten the headline's children.
            const swapTargets = target.querySelectorAll('.animate-digital-text')
            const swapTarget = swapTargets[swapTargets.length - 1]
            if (swapTarget) timeline.add(() => this.textSwapAnimation(swapTarget), BEAT.textSwap)
        })
    }

    strobe(timeline, targets) {
        if (!targets.length) return
        targets.forEach((el) => el.classList.remove('visibility-hidden'))

        timeline.from(targets, { opacity: 0 }, BEAT.accent).to(
            targets,
            {
                opacity: 0.1,
                duration: 0.75,
                repeat: STROBE_REPEAT,
                yoyo: true,
                ease: 'power1.inOut',
            },
            '>',
        )
    }

    revealAccordionItems() {
        if (!this.hasAccordionItemTarget) return

        gsap.set(this.accordionItemTargets, { opacity: 0, y: MOTION.y })
        gsap.to(this.accordionItemTargets, {
            opacity: 1,
            y: 0,
            duration: MOTION.duration,
            ease: MOTION.ease,
            stagger: MOTION.stagger,
            scrollTrigger: {
                trigger: this.accordionItemTargets[0].parentElement,
                start: MOTION.start,
                once: true,
            },
        })
    }

    // Paused off screen so idle sections stop repainting.
    initPatternStrobe() {
        const overlay =
            this.element.closest('.rtm-pattern') || this.element.querySelector('.rtm-pattern')
        if (!overlay || overlay.dataset.strobeInitialized) return

        overlay.dataset.strobeInitialized = 'true'
        const strobe = gsap
            .timeline({ repeat: -1, paused: true })
            .call(() => overlay.classList.remove('strobe'))
            .to({}, { duration: 2.5 })
            .call(() => overlay.classList.add('strobe'))
            .to({}, { duration: 1 })

        ScrollTrigger.create({
            trigger: overlay,
            start: 'top bottom',
            end: 'bottom top',
            animation: strobe,
            toggleActions: 'play pause resume pause',
        })
    }

    disconnect() {
        if (this._initFrame) {
            cancelAnimationFrame(this._initFrame)
            this._initFrame = null
        }
        this.constructor._connectedControllers = Math.max(
            (this.constructor._connectedControllers || 1) - 1,
            0,
        )
        if (this.constructor._connectedControllers === 0 && this.constructor._refreshTimeout) {
            clearTimeout(this.constructor._refreshTimeout)
            this.constructor._refreshTimeout = null
        }

        if (this.mediaQueries) {
            this.mediaQueries.revert()
            this.mediaQueries = null
        }

        if (this._splitTexts) {
            this._splitTexts.forEach((split) => split.revert())
            this._splitTexts = []
        }

        this.element.querySelectorAll('[data-original-html]').forEach((el) => {
            el.innerHTML = el.dataset.originalHtml
            delete el.dataset.originalHtml
        })

        this.revealAll()

        const overlay =
            this.element.closest('.rtm-pattern') || this.element.querySelector('.rtm-pattern')
        if (overlay) delete overlay.dataset.strobeInitialized
    }

    splitIntoCharSpans(target) {
        if (!target.dataset.originalHtml) target.dataset.originalHtml = target.innerHTML

        const chars = target.textContent.trim().split('')
        target.innerHTML = ' '
        chars.forEach((char) => {
            const span = document.createElement('span')
            span.innerHTML = char === ' ' ? '&nbsp;' : char
            target.appendChild(span)
        })

        return target.querySelectorAll('span')
    }

    textSwapAnimation(target) {
        const originalText = target.textContent.trim()

        gsap.fromTo(
            this.splitIntoCharSpans(target),
            { opacity: 1, visibility: 'visible' },
            {
                opacity: 0,
                visibility: 'hidden',
                stagger: { each: 0.05, from: 'end' },
                duration: 0.25,
                ease: 'power1.inOut',
                onComplete: () => {
                    const substituteText = target.dataset.substituteText || originalText
                    target.textContent = ''

                    substituteText.split('').forEach((char) => {
                        const span = document.createElement('span')
                        span.textContent = char
                        target.appendChild(span)
                    })

                    gsap.fromTo(
                        target.querySelectorAll('span'),
                        { opacity: 0, visibility: 'hidden' },
                        {
                            opacity: 1,
                            visibility: 'visible',
                            stagger: { each: 0.05, from: 'start' },
                            duration: 0.25,
                            ease: 'power1.inOut',
                        },
                    )
                },
            },
        )
    }
}
